import {
  Button as ChakraButton,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  useDisclosure,
  Text,
  Flex,
  BoxProps,
} from '@chakra-ui/react';
import { FaAngleDown } from 'react-icons/fa';
import { IoIosCloseCircle } from 'react-icons/io';
import { MouseEvent, ReactNode, useEffect, useState, useCallback } from 'react';

import Button from 'components/atoms/button';
import Heading from 'components/atoms/heading';
import { PropsWithChildren } from 'lib/@types';
import { MobileTooltip } from 'components/atoms/mobileTooltip';
import {
  useJobFilters,
  JobFiltersType,
  DEFAULT_VALUE,
} from 'lib/repositories/jobsShowcaseFilters';

export type MobileFilterCardProps = {
  label: string;
  title: string;
  resetFilter?: () => void;
  showResetFilter: boolean;
  tooltipContent?: ReactNode;
  showTooltip: boolean;
  handleSearchJobs?: () => void;
  isActive: boolean;
  defaultSalaryValues?: Array<number>;
} & PropsWithChildren &
  BoxProps;

export const MobileFilterCard = ({
  label,
  title,
  children,
  resetFilter,
  showResetFilter,
  tooltipContent,
  showTooltip,
  handleSearchJobs,
  isActive,
  ...props
}: MobileFilterCardProps) => {
  const { filters, setFilters } = useJobFilters();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [shouldReset, setShouldReset] = useState(false);
  const [filterSnapshot, setFilterSnapshot] = useState<JobFiltersType>({
    ...DEFAULT_VALUE,
  });

  const handleFilter = useCallback(() => {
    if (handleSearchJobs) handleSearchJobs();
    onClose();
  }, [handleSearchJobs, onClose]);

  const handleResetFilter = (e: MouseEvent) => {
    if (!resetFilter) return;
    e.stopPropagation();
    resetFilter();
    setShouldReset(true);
  };

  const handleOpenModal = () => {
    setFilterSnapshot({ ...filters });
    onOpen();
  };

  const closeAndRevert = () => {
    setFilters({ ...filterSnapshot });
    onClose();
  };

  useEffect(() => {
    if (shouldReset) handleFilter();
    setShouldReset(false);
  }, [filters, handleFilter, shouldReset]);

  return (
    <>
      <ChakraButton
        bgColor={isActive ? 'primary.150' : 'white'}
        borderColor={isActive ? 'primary.500' : 'grey.100'}
        borderRadius={18}
        borderWidth={1}
        color={isActive ? 'primary.500' : 'text.700'}
        fontSize={14}
        fontWeight={600}
        _hover={{
          borderColor: isActive ? 'primary.500' : 'text.700',
        }}
        _focus={{
          borderColor: isActive ? 'primary.500' : 'text.700',
        }}
        rightIcon={
          <>
            <FaAngleDown
              color={isActive ? 'primary.500' : ''}
              style={{ marginTop: '3px', marginLeft: '-5px' }}
            />
            {showResetFilter && isActive ? (
              <IoIosCloseCircle
                onClick={handleResetFilter}
                style={{ marginLeft: '6px', width: '18px', height: '18px' }}
              />
            ) : null}
          </>
        }
        onClick={handleOpenModal}
        px={3}
        py={2}
        flexShrink='0'
      >
        {label}
      </ChakraButton>

      <Drawer placement='bottom' onClose={closeAndRevert} isOpen={isOpen}>
        <DrawerOverlay />

        <DrawerContent
          maxH='-webkit-fill-available'
          borderTopRadius='base'
          p={5}
          {...props}
        >
          <DrawerCloseButton mt={3} mr={3} />

          <DrawerHeader p={0} mb={6}>
            <Flex flexDirection='row' alignItems='center'>
              <Heading
                pt={0}
                ml={0}
                my={0}
                mr={2}
                letterSpacing='tight'
                fontFamily="'Poppins', sans-serif"
                format='h2'
                fontWeight={600}
                color='text.900'
              >
                {title}
              </Heading>
              {showTooltip ? (
                <MobileTooltip showBackground={false} mr={5}>
                  {tooltipContent}
                </MobileTooltip>
              ) : null}
              {showResetFilter && isActive ? (
                <Text
                  fontSize='xs'
                  fontWeight={400}
                  letterSpacing='tight'
                  color='primary.500'
                  textDecoration='underline'
                  cursor='pointer'
                  onClick={resetFilter}
                >
                  Limpar
                </Text>
              ) : null}
            </Flex>
          </DrawerHeader>

          <DrawerBody p={0}>{children}</DrawerBody>

          <DrawerFooter p={0} mt={7}>
            <Button w='100%' size='xl' onClick={handleFilter}>
              Confirmar
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};
